/**
 * Generic list + edit pages for CMS collections (pages, services, industries,
 * news_posts, projects, gallery_items). Also handles singletons (navigation,
 * footer_config, site_settings).
 * We keep this in one file to avoid file-count sprawl — each variant is a
 * thin wrapper around the same primitives.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Loader2, Plus, Save, Trash2 } from "lucide-react";
import { adminApi } from "@/admin/adminApi";
import AdminLayout from "@/admin/AdminLayout";
import SectionsEditor from "@/admin/SectionsEditor";
import { toast } from "@/admin/Toast";

const COLL_META = {
  pages: { label: "Pages", singular: "page", key: "slug", sections: true, publicPrefix: "" },
  services: { label: "Services", singular: "service", key: "slug", sections: true, publicPrefix: "/services" },
  industries: { label: "Industries", singular: "industry", key: "slug", sections: true, publicPrefix: "/industries" },
  news_posts: { label: "News posts", singular: "news post", key: "slug", sections: false, publicPrefix: "/news" },
  projects: { label: "Projects", singular: "project", key: "slug", sections: false, publicPrefix: "/projects" },
  gallery_items: { label: "Gallery items", singular: "gallery item", key: "id", sections: false, publicPrefix: "" },
};

// ---------------- List page ----------------
export function CollectionListPage({ name }) {
  const meta = COLL_META[name];
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    adminApi.get(`/collections/${name}`).then((r) => setItems(r.data));
  }, [name]);

  const filtered = useMemo(() => {
    if (!items) return [];
    const query = q.trim().toLowerCase();
    if (!query) return items;
    return items.filter((it) =>
      Object.values(it).some((v) => typeof v === "string" && v.toLowerCase().includes(query))
    );
  }, [items, q]);

  async function onDelete(key) {
    if (!window.confirm(`Delete this ${meta.singular}?`)) return;
    try {
      await adminApi.delete(`/collections/${name}/${key}`);
      setItems((all) => all.filter((it) => it[meta.key] !== key));
      toast("success", `${meta.singular[0].toUpperCase() + meta.singular.slice(1)} deleted`);
    } catch (e) {
      toast("error", e?.response?.data?.detail || "Delete failed");
    }
  }

  return (
    <AdminLayout title={meta.label}>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search…"
          data-testid={`list-${name}-search`}
          className="h-10 px-3 bg-white border border-mist-soft rounded-md text-sm w-full sm:w-64 focus:outline-none focus:border-mist-gold"
        />
        <div className="ml-auto" />
        <Link
          to={`/admin/${name}/__new__`}
          data-testid={`list-${name}-new`}
          className="inline-flex items-center gap-2 bg-mist-navy text-white px-4 h-10 text-[11px] uppercase tracking-[0.22em] font-semibold rounded-full hover:bg-mist-navy/90"
        >
          <Plus className="h-4 w-4" /> Add {meta.singular}
        </Link>
      </div>

      {!items ? (
        <div className="py-20 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-mist-navy" /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-mist-soft rounded-md p-10 text-center text-sm text-mist-ink/60">
          No {meta.label.toLowerCase()} yet.
        </div>
      ) : (
        <div className="bg-white border border-mist-soft rounded-md overflow-hidden" data-testid={`list-${name}`}>
          <table className="w-full text-sm">
            <thead className="bg-mist-off text-[11px] uppercase tracking-[0.22em] text-mist-navy/60">
              <tr>
                <th className="px-4 py-3 text-left">{meta.key === "slug" ? "Slug" : "ID"}</th>
                <th className="px-4 py-3 text-left">Title</th>
                <th className="px-4 py-3 text-left">Updated</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((it) => (
                <tr key={it[meta.key]} className="border-t border-mist-soft/70">
                  <td className="px-4 py-3 font-mono text-xs">{it[meta.key]}</td>
                  <td className="px-4 py-3">{it.title || it.caption || "—"}</td>
                  <td className="px-4 py-3 text-xs text-mist-ink/60">{(it.updated_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-3 text-right">
                    <Link
                      to={`/admin/${name}/${encodeURIComponent(it[meta.key])}`}
                      data-testid={`list-${name}-edit-${it[meta.key]}`}
                      className="text-xs text-mist-navy hover:text-mist-gold uppercase tracking-widest mr-3"
                    >
                      Edit
                    </Link>
                    <button
                      onClick={() => onDelete(it[meta.key])}
                      data-testid={`list-${name}-delete-${it[meta.key]}`}
                      className="text-xs text-mist-navy/50 hover:text-red-600 uppercase tracking-widest"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}

// ---------------- Edit page ----------------
export function CollectionEditPage({ name }) {
  const meta = COLL_META[name];
  const { key } = useParams();
  const isNew = key === "__new__";
  const [doc, setDoc] = useState(isNew ? { [meta.key]: "", title: "", seo: {}, sections: [] } : null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (isNew) return;
    adminApi.get(`/collections/${name}/${encodeURIComponent(key)}`).then((r) => setDoc(r.data));
  }, [name, key, isNew]);

  useEffect(() => {
    if (!dirty) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const set = (patch) => {
    setDoc((d) => ({ ...d, ...patch }));
    setDirty(true);
  };
  const setSeo = (patch) => {
    setDoc((d) => ({ ...d, seo: { ...(d?.seo || {}), ...patch } }));
    setDirty(true);
  };

  async function onSave() {
    if (!doc?.[meta.key]) {
      toast("error", `A ${meta.key} is required`);
      return;
    }
    setSaving(true);
    try {
      const targetKey = doc[meta.key];
      await adminApi.put(`/collections/${name}/${encodeURIComponent(targetKey)}`, doc);
      toast("success", "Saved");
      setDirty(false);
      if (isNew) nav(`/admin/${name}/${encodeURIComponent(targetKey)}`, { replace: true });
    } catch (e) {
      toast("error", e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  if (!doc) {
    return (
      <AdminLayout title={`Edit ${meta.singular}`}>
        <div className="py-20 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-mist-navy" /></div>
      </AdminLayout>
    );
  }

  const publicHref = meta.publicPrefix
    ? `${meta.publicPrefix}/${doc[meta.key]}`
    : (name === "pages" ? `/${doc.slug === "home" ? "" : doc.slug}` : null);

  return (
    <AdminLayout title={isNew ? `New ${meta.singular}` : `${meta.label} · ${doc[meta.key]}`}>
      <div className="flex items-center gap-3 mb-6">
        <Link to={`/admin/${name}`} className="text-xs uppercase tracking-widest text-mist-navy/70 hover:text-mist-gold">← Back to {meta.label}</Link>
        {publicHref && (
          <a
            href={publicHref}
            target="_blank"
            rel="noreferrer"
            data-testid="edit-view-public"
            className="text-xs uppercase tracking-widest text-mist-navy/70 hover:text-mist-gold ml-auto"
          >
            View public →
          </a>
        )}
      </div>

      <div className="bg-white border border-mist-soft rounded-md p-6 mb-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.22em] text-mist-navy font-medium mb-1.5">{meta.key === "slug" ? "Slug" : "ID"}</span>
            <input
              value={doc[meta.key] || ""}
              onChange={(e) => set({ [meta.key]: e.target.value })}
              disabled={!isNew}
              data-testid="edit-key"
              className="w-full h-10 px-3 bg-mist-off border border-mist-soft rounded-md text-sm disabled:opacity-70 focus:outline-none focus:border-mist-gold font-mono"
            />
          </label>
          <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.22em] text-mist-navy font-medium mb-1.5">Title</span>
            <input
              value={doc.title || ""}
              onChange={(e) => set({ title: e.target.value })}
              data-testid="edit-title"
              className="w-full h-10 px-3 bg-mist-off border border-mist-soft rounded-md text-sm focus:outline-none focus:border-mist-gold"
            />
          </label>
        </div>

        {name === "news_posts" && (
          <div className="mt-4">
            <label className="block mb-3">
              <span className="block text-[11px] uppercase tracking-[0.22em] text-mist-navy font-medium mb-1.5">Excerpt</span>
              <textarea
                rows={3}
                value={doc.excerpt || ""}
                onChange={(e) => set({ excerpt: e.target.value })}
                className="w-full text-sm px-3 py-2 bg-mist-off border border-mist-soft rounded-md focus:outline-none focus:border-mist-gold"
              />
            </label>
            <label className="block mb-3">
              <span className="block text-[11px] uppercase tracking-[0.22em] text-mist-navy font-medium mb-1.5">Cover image URL</span>
              <input
                value={doc.cover_image || ""}
                onChange={(e) => set({ cover_image: e.target.value })}
                className="w-full h-10 px-3 bg-mist-off border border-mist-soft rounded-md text-sm focus:outline-none focus:border-mist-gold"
              />
            </label>
            <label className="block">
              <span className="block text-[11px] uppercase tracking-[0.22em] text-mist-navy font-medium mb-1.5">Published at (ISO)</span>
              <input
                value={doc.published_at || ""}
                onChange={(e) => set({ published_at: e.target.value })}
                placeholder="2026-02-15T09:00:00Z"
                className="w-full h-10 px-3 bg-mist-off border border-mist-soft rounded-md text-sm focus:outline-none focus:border-mist-gold"
              />
            </label>
          </div>
        )}

        {name === "gallery_items" && (
          <div className="mt-4">
            <label className="block mb-3">
              <span className="block text-[11px] uppercase tracking-[0.22em] text-mist-navy font-medium mb-1.5">Image URL</span>
              <input
                value={doc.image_url || ""}
                onChange={(e) => set({ image_url: e.target.value })}
                className="w-full h-10 px-3 bg-mist-off border border-mist-soft rounded-md text-sm focus:outline-none focus:border-mist-gold"
              />
            </label>
            <label className="block mb-3">
              <span className="block text-[11px] uppercase tracking-[0.22em] text-mist-navy font-medium mb-1.5">Caption</span>
              <input
                value={doc.caption || ""}
                onChange={(e) => set({ caption: e.target.value })}
                className="w-full h-10 px-3 bg-mist-off border border-mist-soft rounded-md text-sm focus:outline-none focus:border-mist-gold"
              />
            </label>
            <label className="block">
              <span className="block text-[11px] uppercase tracking-[0.22em] text-mist-navy font-medium mb-1.5">Order</span>
              <input
                type="number"
                value={doc.order ?? 0}
                onChange={(e) => set({ order: Number(e.target.value) })}
                className="w-24 h-10 px-3 bg-mist-off border border-mist-soft rounded-md text-sm focus:outline-none focus:border-mist-gold"
              />
            </label>
          </div>
        )}
      </div>

      {meta.sections && (
        <>
          <h2 className="text-[11px] uppercase tracking-[0.22em] text-mist-navy/60 font-semibold mb-3">Sections</h2>
          <SectionsEditor sections={doc.sections || []} onChange={(v) => set({ sections: v })} />
        </>
      )}

      <div className="mt-8 bg-white border border-mist-soft rounded-md p-5">
        <h2 className="text-[11px] uppercase tracking-[0.22em] text-mist-navy/60 font-semibold mb-3">SEO</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {["title", "description", "canonical", "og_title", "og_description", "og_image"].map((f) => (
            <label key={f} className="block">
              <span className="block text-[11px] uppercase tracking-[0.22em] text-mist-navy font-medium mb-1.5">{f.replace(/_/g, " ")}</span>
              <input
                value={(doc.seo && doc.seo[f]) || ""}
                onChange={(e) => setSeo({ [f]: e.target.value })}
                data-testid={`edit-seo-${f}`}
                className="w-full h-10 px-3 bg-mist-off border border-mist-soft rounded-md text-sm focus:outline-none focus:border-mist-gold"
              />
            </label>
          ))}
        </div>
      </div>

      <div className="sticky bottom-4 mt-8 flex items-center gap-3">
        <button
          onClick={onSave}
          disabled={saving}
          data-testid="edit-save"
          className="inline-flex items-center gap-2 bg-mist-gold text-mist-navy px-6 h-11 text-[11px] uppercase tracking-[0.22em] font-semibold rounded-full hover:bg-mist-gold/90 disabled:opacity-60 shadow-lg"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save
        </button>
        <Link
          to={`/admin/${name}`}
          data-testid="edit-cancel"
          className="text-xs uppercase tracking-widest text-mist-navy/70 hover:text-mist-gold"
          onClick={(e) => {
            if (dirty && !window.confirm("Discard unsaved changes?")) e.preventDefault();
          }}
        >
          Cancel
        </Link>
        {dirty && <span className="text-xs text-mist-ink/60" data-testid="edit-dirty">Unsaved changes</span>}
      </div>
    </AdminLayout>
  );
}

// ---------------- Singleton editor (navigation / footer_config / site_settings) ----------------
export function SingletonEditPage({ name, title }) {
  const [doc, setDoc] = useState(null);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    adminApi.get(`/singleton/${name}`).then((r) => {
      setDoc(r.data);
      setText(JSON.stringify(r.data, null, 2));
    });
  }, [name]);

  async function onSave() {
    try {
      const parsed = JSON.parse(text);
      setSaving(true);
      await adminApi.put(`/singleton/${name}`, parsed);
      toast("success", "Saved");
      setDoc(parsed);
      setErr(null);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Save failed";
      setErr(String(msg));
      toast("error", "Save failed — check JSON validity");
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminLayout title={title}>
      {!doc ? (
        <div className="py-20 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-mist-navy" /></div>
      ) : (
        <div className="bg-white border border-mist-soft rounded-md p-5">
          <p className="text-sm text-mist-ink/70 mb-3">
            Edit the JSON structure below. Refer to the public site for the shape of each field.
          </p>
          <textarea
            data-testid={`singleton-${name}-json`}
            rows={28}
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="w-full text-xs font-mono px-3 py-2 bg-mist-off border border-mist-soft rounded-md focus:outline-none focus:border-mist-gold"
          />
          {err && <div className="mt-2 text-xs text-red-600" role="alert">{err}</div>}
          <div className="mt-4">
            <button
              onClick={onSave}
              disabled={saving}
              data-testid={`singleton-${name}-save`}
              className="inline-flex items-center gap-2 bg-mist-gold text-mist-navy px-6 h-11 text-[11px] uppercase tracking-[0.22em] font-semibold rounded-full hover:bg-mist-gold/90 disabled:opacity-60"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
            </button>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
